// frontend/src/utils/logger.ts
// Professional logging utility with levels and production safety

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const REDACTED_IDENTITY = '[IDENTITY_REDACTED]';
const REDACTED_SECRET = '[SECRET_REDACTED]';
const REDACTED_PRIVATE_CONTEXT = '[PRIVATE_CONTEXT_REDACTED]';
const MAX_LOG_DEPTH = 6;
const MAX_LOG_ARRAY_ITEMS = 50;
const MAX_LOG_OBJECT_KEYS = 100;

const SECRET_KEYS = new Set([
  'accesstoken', 'apikey', 'authorization', 'cookie', 'cookies', 'jwt',
  'password', 'refreshtoken', 'secret', 'sessiontoken', 'token',
]);

const IDENTITY_KEYS = new Set([
  'actionid', 'adminemail', 'adminemails', 'alertid', 'canonicaluserid',
  'cohortid', 'contactemail', 'contactid', 'documentid',
  'displayname', 'email', 'firstname', 'fullname', 'lastname', 'loginemail',
  'memberid', 'name', 'nickname', 'organizationid', 'phonenumber',
  'primaryemail', 'projectid', 'reportid', 'riskid', 'sessionid', 'userid',
  'username', 'wixaccountid', 'wixmemberid',
]);

const PRIVATE_CONTEXT_KEYS = new Set([
  'action', 'address', 'addresses', 'attribution', 'backendsettings', 'content',
  'conversion', 'customfields', 'description', 'idea', 'location', 'messagebody',
  'metadata', 'phone', 'phones', 'profile', 'project', 'projectname', 'prompt',
  'query', 'referrer', 'report', 'reports', 'selectedproject', 'sessiondata',
  'settings', 'text', 'user', 'userobject',
]);

const normalizeLogKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '');

const sanitizeLogString = (value: string): string => value
  .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[EMAIL_REDACTED]')
  .replace(/\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi, 'Bearer [TOKEN_REDACTED]')
  .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[TOKEN_REDACTED]')
  .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, '[IDENTIFIER_REDACTED]')
  .replace(/\b(?:usr|user|member|contact|report|project|action|risk|cohort|org|organization|session|wix)[_-][A-Za-z0-9_-]+\b/gi, '[IDENTIFIER_REDACTED]')
  .replace(/([?&](?:access_token|auth|code|jwt|password|refresh_token|session|token)=)[^&#\s]+/gi, '$1[REDACTED]');

export const sanitizeLogValue = (
  value: unknown,
  seen: WeakSet<object> = new WeakSet<object>(),
  depth = 0,
): unknown => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return sanitizeLogString(value);
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return value;
  if (typeof value === 'function') return `[Function ${value.name || 'anonymous'}]`;
  if (typeof value !== 'object') return String(value);
  if (depth >= MAX_LOG_DEPTH) return '[DEPTH_LIMIT]';
  if (seen.has(value)) return '[CIRCULAR]';
  seen.add(value);

  if (value instanceof Date) return value.toISOString();
  if (value instanceof Error) {
    const code = 'code' in value && typeof value.code === 'string' ? value.code : undefined;
    return {
      name: value.name,
      message: sanitizeLogString(value.message),
      ...(code ? { code } : {}),
    };
  }
  if (Array.isArray(value)) {
    const sanitized = value
      .slice(0, MAX_LOG_ARRAY_ITEMS)
      .map(item => sanitizeLogValue(item, seen, depth + 1));
    if (value.length > MAX_LOG_ARRAY_ITEMS) {
      sanitized.push(`[${value.length - MAX_LOG_ARRAY_ITEMS} MORE ITEMS]`);
    }
    return sanitized;
  }

  const output: Record<string, unknown> = {};
  const entries = Object.entries(value as Record<string, unknown>);
  for (const [key, nestedValue] of entries.slice(0, MAX_LOG_OBJECT_KEYS)) {
    const normalizedKey = normalizeLogKey(key);
    if (SECRET_KEYS.has(normalizedKey)) {
      output[key] = REDACTED_SECRET;
    } else if (IDENTITY_KEYS.has(normalizedKey)) {
      output[key] = REDACTED_IDENTITY;
    } else if (PRIVATE_CONTEXT_KEYS.has(normalizedKey)) {
      output[key] = REDACTED_PRIVATE_CONTEXT;
    } else {
      output[key] = sanitizeLogValue(nestedValue, seen, depth + 1);
    }
  }
  if (entries.length > MAX_LOG_OBJECT_KEYS) {
    output.__truncatedKeys = entries.length - MAX_LOG_OBJECT_KEYS;
  }
  return output;
};

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  timestamp: boolean;
}

class Logger {
  private config: LoggerConfig;
  private readonly levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  constructor() {
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    this.config = {
      enabled: isDevelopment || process.env.REACT_APP_ENABLE_LOGS === 'true',
      level: (process.env.REACT_APP_LOG_LEVEL as LogLevel) || 'info',
      timestamp: true,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    return this.levels[level] >= this.levels[this.config.level];
  }

  private formatMessage(level: LogLevel, ...args: unknown[]): unknown[] {
    const safeArgs = args.map(arg => sanitizeLogValue(arg));
    if (!this.config.timestamp) return safeArgs;
    
    const timestamp = new Date().toISOString();
    const levelEmoji = {
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
    };
    
    return [`[${timestamp}] ${levelEmoji[level]}`, ...safeArgs];
  }

  /**
   * Debug level - detailed information for debugging
   * Only shows in development with debug level
   */
  debug(...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.log(...this.formatMessage('debug', ...args));
    }
  }

  /**
   * Info level - general informational messages
   * Shows in development by default
   */
  info(...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.info(...this.formatMessage('info', ...args));
    }
  }

  /**
   * Warn level - warning messages that need attention
   * Shows in development and production
   */
  warn(...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatMessage('warn', ...args));
    }
  }

  /**
   * Error level - error messages and exceptions
   * Always shows (even in production)
   */
  error(...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(...this.formatMessage('error', ...args));
    }
  }

  /**
   * Group logs together (useful for complex operations)
   */
  group(label: string): void {
    if (this.config.enabled) {
      console.group(sanitizeLogString(label));
    }
  }

  groupEnd(): void {
    if (this.config.enabled) {
      console.groupEnd();
    }
  }

  /**
   * Table output for arrays/objects
   */
  table(data: unknown): void {
    if (this.config.enabled) {
      console.table(sanitizeLogValue(data));
    }
  }

  /**
   * Temporarily override log level for specific operations
   */
  withLevel(level: LogLevel, callback: () => void): void {
    const originalLevel = this.config.level;
    this.config.level = level;
    callback();
    this.config.level = originalLevel;
  }

  /**
   * Get current configuration
   */
  getConfig(): Readonly<LoggerConfig> {
    return { ...this.config };
  }
}

// Export singleton instance
export const logger = new Logger();

// For convenience, also export as default
export default logger;

// Export types
export type { LogLevel, LoggerConfig };
